import { useRef, useState, useCallback } from "react";

/**
 * Detect iOS Safari for codec/format selection.
 */
function isIOSSafari() {
	return (
		/iPad|iPhone|iPod/.test(navigator.userAgent) &&
		/WebKit/.test(navigator.userAgent) &&
		!/CriOS/.test(navigator.userAgent) &&
		!/FxiOS/.test(navigator.userAgent)
	);
}

/**
 * Choose the best supported audio MIME type for MediaRecorder.
 */
function pickAudioMimeType() {
	const ios = isIOSSafari();
	const candidates = ios
		? [
				"audio/mp4",
				"audio/aac",
				"audio/mpeg",
				"audio/wav",
				"audio/webm;codecs=opus",
				"audio/webm",
			]
		: [
				"audio/wav",
				"audio/webm;codecs=opus",
				"audio/webm",
				"audio/mp4",
				"audio/ogg;codecs=opus",
			];

	for (const mime of candidates) {
		if (MediaRecorder.isTypeSupported(mime)) {
			return mime;
		}
	}
	return null;
}

/**
 * Extract a static waveform (RMS amplitude array) from an AudioBuffer.
 */
function extractStaticWaveform(audioBuffer, samples = 1024) {
	const channelData = audioBuffer.getChannelData(0);
	if (!channelData || channelData.length === 0) {
		return new Array(samples).fill(128);
	}

	const blockSize = Math.floor(channelData.length / samples);
	const waveform = [];
	for (let i = 0; i < samples; i++) {
		const startIdx = i * blockSize;
		const endIdx = Math.min(startIdx + blockSize, channelData.length);

		let sumSquares = 0;
		let count = 0;
		for (let j = startIdx; j < endIdx; j++) {
			sumSquares += channelData[j] * channelData[j];
			count++;
		}
		const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
		waveform.push(Math.max(0, Math.min(255, 128 + rms * 127)));
	}
	return waveform;
}

/**
 * Validate and process a recorded audio blob:
 * decode-test, extract static waveform, create object URL.
 * Returns { audioUrl, waveform } on success, or throws on invalid audio.
 */
async function processAudioBlob(chunks, recorderMime, setError) {
	if (chunks.length === 0) {
		if (import.meta.env.DEV) {
			console.error("No audio data recorded");
		}
		setError("No audio data was recorded. Please try again.");
		return null;
	}

	const audioBlob = new Blob(chunks, { type: recorderMime });

	if (import.meta.env.DEV) {
		console.log(
			"Audio blob created:",
			audioBlob.size,
			"bytes, type:",
			recorderMime
		);
	}

	if (audioBlob.size === 0) {
		setError("Audio recording is empty. Please try again.");
		return null;
	}

	// Validate decodable and extract static waveform in a single pass
	let waveform;
	try {
		const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
		const buf = await audioBlob.arrayBuffer();
		const audioBuffer = await audioCtx.decodeAudioData(buf);

		if (import.meta.env.DEV && audioBuffer.duration > 0) {
			console.log("Audio duration:", audioBuffer.duration, "seconds");
		}

		waveform = extractStaticWaveform(audioBuffer);
		audioCtx.close();
	} catch {
		setError("Recorded audio is invalid. Please try recording again.");
		return null;
	}

	return { audioBlob, audioUrl: URL.createObjectURL(audioBlob), waveform };
}

/**
 * useAudioRecording — encapsulates microphone recording, waveform
 * visualization (live + static), and blob creation.
 */
export default function useAudioRecording({
	streamRef,
	setStream,
	showPreview,
	onCapture,
	onClose,
	duckBgm,
	restoreBgm,
	setError,
	setHasPermission,
}) {
	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);
	const audioRecorderRef = useRef(null);
	const audioContextRef = useRef(null);
	const analyserRef = useRef(null);
	const animationFrameRef = useRef(null);
	const isAudioRecordingRef = useRef(false);

	const [isAudioRecording, setIsAudioRecording] = useState(false);
	const [audioLevels, setAudioLevels] = useState([]);
	const [capturedAudio, setCapturedAudio] = useState(null);
	const [isUploadedAudio, setIsUploadedAudio] = useState(false);

	// Refs to avoid stale closures in onstop / event handlers
	const showPreviewRef = useRef(showPreview);
	const onCaptureRef = useRef(onCapture);
	const onCloseRef = useRef(onClose);
	const stopAudioRecordingRef = useRef(null);
	showPreviewRef.current = showPreview;
	onCaptureRef.current = onCapture;
	onCloseRef.current = onClose;

	const startAudioRecording = useCallback(async () => {
		try {
			setError(null);

			// Stop camera stream if running
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
				streamRef.current = null;
				setStream(null);
			}

			const audioStream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});
			audioRecorderRef.current = audioStream;

			// Detect microphone disconnect — use ref to get current recording state
			const handleAudioTrackEnded = () => {
				if (isAudioRecordingRef.current) {
					stopAudioRecordingRef.current?.();
					setError(
						"Microphone connection lost during recording. Please check your microphone and try again."
					);
				} else {
					setError(
						"Microphone connection lost. Please check your microphone and try again."
					);
				}
				setHasPermission(false);

				if (audioRecorderRef.current) {
					audioRecorderRef.current.getTracks().forEach((t) => t.stop());
					audioRecorderRef.current = null;
				}
			};

			audioStream.getAudioTracks().forEach((track) => {
				track.addEventListener("ended", handleAudioTrackEnded);
			});

			// Set up Web Audio analyser for live visualization
			audioContextRef.current = new (
				window.AudioContext || window.webkitAudioContext
			)();
			if (audioContextRef.current.state === "suspended") {
				await audioContextRef.current.resume();
			}

			analyserRef.current = audioContextRef.current.createAnalyser();
			const source =
				audioContextRef.current.createMediaStreamSource(audioStream);
			source.connect(analyserRef.current);
			analyserRef.current.fftSize = 2048;
			analyserRef.current.smoothingTimeConstant = 0.8;

			// Create MediaRecorder with best supported MIME type
			const mimeType = pickAudioMimeType();
			if (import.meta.env.DEV) {
				console.log("Audio: Using MIME type:", mimeType || "(default)");
			}
			try {
				mediaRecorderRef.current = mimeType
					? new MediaRecorder(audioStream, { mimeType })
					: new MediaRecorder(audioStream);
			} catch {
				try {
					mediaRecorderRef.current = new MediaRecorder(audioStream);
				} catch (fallbackError) {
					console.error("MediaRecorder creation failed:", fallbackError);
					setError(
						"Audio recording is not supported on this device. Please try a different browser."
					);
					return;
				}
			}

			recordedChunksRef.current = [];

			mediaRecorderRef.current.ondataavailable = (e) => {
				if (e.data && e.data.size > 0) {
					recordedChunksRef.current.push(e.data);
				}
			};

			mediaRecorderRef.current.onstop = () => {
				const iosSafari = isIOSSafari();

				const finalizeRecording = async () => {
					// Flush any remaining data
					if (
						mediaRecorderRef.current &&
						mediaRecorderRef.current.state !== "inactive"
					) {
						try {
							mediaRecorderRef.current.requestData();
						} catch {
							// Ignore if already stopped
						}
					}

					await new Promise((resolve) => {
						setTimeout(resolve, 200);
					});

					const recorderMime =
						mediaRecorderRef.current?.mimeType ||
						(iosSafari ? "audio/mp4" : "audio/webm");

					const result = await processAudioBlob(
						recordedChunksRef.current,
						recorderMime,
						setError
					);

					if (!result) {
						// processAudioBlob already called setError
						if (audioRecorderRef.current) {
							audioRecorderRef.current
								.getTracks()
								.forEach((track) => track.stop());
						}
						return;
					}

					setAudioLevels(result.waveform);

					setTimeout(() => {
						setCapturedAudio(result.audioUrl);
						setIsUploadedAudio(false);
					}, 100);

					if (audioRecorderRef.current) {
						audioRecorderRef.current
							.getTracks()
							.forEach((track) => track.stop());
					}

					restoreBgm?.();

					if (animationFrameRef.current) {
						cancelAnimationFrame(animationFrameRef.current);
						animationFrameRef.current = null;
					}

					if (!showPreviewRef.current) {
						const file = new File(
							[result.audioBlob],
							`audio-${Date.now()}.webm`,
							{ type: recorderMime }
						);
						onCaptureRef.current(file);
						onCloseRef.current("completed");
					}
				};

				setTimeout(() => {
					finalizeRecording().catch((err) => {
						console.error("Error finalizing audio recording:", err);
						setError(
							"Something went wrong processing your recording. Please try again."
						);
					});
				}, 500);
			};

			mediaRecorderRef.current.start(100);
			setIsAudioRecording(true);
			isAudioRecordingRef.current = true;

			duckBgm(0);

			// Live visualization loop
			const bufferLength = analyserRef.current.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			const updateVisualization = () => {
				if (
					!analyserRef.current ||
					!mediaRecorderRef.current ||
					mediaRecorderRef.current.state !== "recording"
				) {
					if (animationFrameRef.current) {
						cancelAnimationFrame(animationFrameRef.current);
						animationFrameRef.current = null;
					}
					return;
				}
				analyserRef.current.getByteTimeDomainData(dataArray);
				setAudioLevels(Array.from(dataArray));
				animationFrameRef.current = requestAnimationFrame(updateVisualization);
			};
			updateVisualization();
		} catch (error) {
			console.error(
				"Error accessing microphone:",
				error?.name,
				error?.message,
				error
			);
			const name = error?.name || error?.constructor?.name || "UnknownError";
			let msg;
			if (name === "NotAllowedError" || name === "PermissionDeniedError") {
				msg =
					"Microphone access was denied. Click the padlock in your browser's address bar, set Microphone to Allow, then reload the page and try again.";
			} else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
				msg = "No microphone found. Please connect a microphone and try again.";
			} else if (name === "NotReadableError" || name === "TrackStartError") {
				msg =
					"Microphone is in use by another app. Close other apps using the microphone (e.g. video calls) and try again.";
			} else if (name === "OverconstrainedError") {
				msg =
					"The requested microphone settings aren't supported by this device.";
			} else if (name === "SecurityError") {
				msg = "Microphone access requires a secure (HTTPS) connection.";
			} else {
				msg = `Could not access microphone (${name}). Please check your browser permissions.`;
			}
			setError(msg);
			setIsAudioRecording(false);
			isAudioRecordingRef.current = false;
		}
	}, [streamRef, setStream, duckBgm, restoreBgm, setError, setHasPermission]);

	const stopAudioRecording = useCallback(() => {
		if (mediaRecorderRef.current && isAudioRecordingRef.current) {
			mediaRecorderRef.current.stop();

			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
				audioContextRef.current = null;
			}
			setAudioLevels([]);
			setIsAudioRecording(false);
			isAudioRecordingRef.current = false;
			restoreBgm?.();
		}
	}, [restoreBgm]);

	// Keep ref in sync so handleAudioTrackEnded always calls the latest version
	stopAudioRecordingRef.current = stopAudioRecording;

	const clearCapturedAudio = useCallback(() => {
		if (capturedAudio) {
			URL.revokeObjectURL(capturedAudio);
		}
		setCapturedAudio(null);
		setIsUploadedAudio(false);
		setAudioLevels([]);
	}, [capturedAudio]);

	const setUploadedAudio = useCallback((url) => {
		setCapturedAudio(url);
		setIsUploadedAudio(true);
	}, []);

	/** Tear down all audio resources (call on unmount). */
	const cleanupAudio = useCallback(() => {
		if (audioRecorderRef.current) {
			audioRecorderRef.current.getTracks().forEach((track) => track.stop());
			audioRecorderRef.current = null;
		}
		if (audioContextRef.current) {
			audioContextRef.current.close();
			audioContextRef.current = null;
		}
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
	}, []);

	return {
		isAudioRecording,
		audioLevels,
		capturedAudio,
		isUploadedAudio,
		startAudioRecording,
		stopAudioRecording,
		clearCapturedAudio,
		setUploadedAudio,
		cleanupAudio,
	};
}
